// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import {
	getFileInfo,
	getFileReactQuery,
	replaceFile,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { DocumentViewer } from '@dealstack/frontend/shared/components/DocumentViewer/DocumentViewer';
import { Expandable } from '@dealstack/frontend/shared/components/Expandable/Expandable';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { GenericBooleanStatus } from '@dealstack/frontend/shared/components/GenericStatus/variants/GenericBooleanStatus/GenericBooleanStatus';
import { TagText, TagPair } from '@dealstack/frontend/shared/components/TagPair/TagPair';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ContractType, SignatureType } from '@dealstack/shared/lib/generated';

interface Props {
	className?: string;
	overridable?: boolean;
	fileId?: string;
	signatureType?: SignatureType;
	buttonText?: string;
	fileType?: ContractType;
	replacementMode?: string;
}

export const FilePreviewByIdModal = styled(
	({
		className,
		overridable,
		fileId,
		signatureType,
		buttonText,
		fileType,
		replacementMode,
	}: Props) => {
		const [showModal, setShowModal] = useState(false);
		const [data, setData] = useState({
			fileName: null,
			fileExtension: null,
			blob: null,
		});
		const fileInfoRequest = useQuery({
			queryKey: [formatUnicorn('/godmode/file/{fileId}/info', { fileId }), { id: fileId }],
			queryFn: getFileInfo,
			enabled: showModal && !!fileId,
		});
		const getDocumentBlobRequest = useQuery({
			queryKey: [API_PATHS.GODMODE_FILE_ID_GET, { id: fileInfoRequest?.data?.payload?.id }],
			queryFn: getFileReactQuery,
			enabled: showModal && !!fileInfoRequest?.data?.payload?.id,
		});

		useEffect(() => {
			if (!getDocumentBlobRequest.isSuccess) {
				return;
			}
			void (async () => {
				const response = getDocumentBlobRequest.data;
				if (!response) {
					return;
				}
				const fileExtension = response.headers.get('fileextension');
				const fileName = response.headers.get('FileName') as string;
				const blob = await response?.blob();
				setData({
					fileName,
					fileExtension,
					blob,
				});
			})();
		}, [getDocumentBlobRequest.isSuccess, getDocumentBlobRequest.data]);
		const toastAPI = useDSToast();

		const replaceFileRequest = useWrappedRequest(replaceFile, undefined, {
			onSuccessDefault: () => {
				toastAPI.success({
					message: 'File replaced successfully',
				});
				void getDocumentBlobRequest.refetch();
				void fileInfoRequest.refetch();
			},
			onAnyErrorDefault: (error) => {
				toastAPI.error({
					message: 'File replace error',
				});
				logger.error(`[FILE REPLACE] ${error}`);
			},
		});

		return (
			<div className={className}>
				{fileId ? (
					<Button onClick={() => setShowModal(true)}>{buttonText}</Button>
				) : (
					<Button disabled>No fileId</Button>
				)}
				{showModal ? (
					<Modal
						className={className}
						onClose={() => setShowModal(false)}
						reactModalProps={{
							style: {
								content: {
									overflow: 'auto',
									height: '50rem',
								},
							},
						}}
					>
						<GridBox>
							<TagPair title="S3 Name">
								<TagText>{fileInfoRequest?.data?.payload?.name}</TagText>
							</TagPair>
							<TagPair title="type">
								<TagText>{fileInfoRequest?.data?.payload?.mimeType}</TagText>
							</TagPair>
							<TagPair title="extension">
								<TagText>{fileInfoRequest?.data?.payload?.extension}</TagText>
							</TagPair>
							<TagPair title="deleted">
								<GenericBooleanStatus
									value={fileInfoRequest?.data?.payload?.deleted}
								/>
							</TagPair>
						</GridBox>
						{overridable ? (
							<OverridableWrapper>
								{signatureType === SignatureType.REVIEW ? (
									<Alert icon="information-filled" state="danger" size="m">
										<AlertTitle>WARNING</AlertTitle>
										<AlertDescription>
											You're going to replace TEMPLATE file. This means that,
											you're not replacing it JUST FOR THIS person, but rather
											FOR ALL PEOPLE THAT HAS THIS FILE (S3 NAME) assigned.
											Think 10 times before you do this, if not sure, call
											CORE team.
										</AlertDescription>
									</Alert>
								) : null}
								{fileType === ContractType.MULTISIGNATURE ? (
									<Alert icon="information-filled" state="danger" size="m">
										<AlertTitle>WARNING</AlertTitle>
										<AlertDescription>
											Overwriting this file will replace it for every investor
											from this MIP
										</AlertDescription>
									</Alert>
								) : null}
								<Expandable initialOpened title="Override file">
									<DragAndDrop
										onDrop={(acceptedFiles: File[]) => {
											const formData = new FormData();
											for (const file of acceptedFiles) {
												formData.append('file', file);
												formData.append('fileSize', file.size.toString());
											}
											replaceFileRequest.fetch({
												fileId,
												formData,
												mode: replacementMode,
											});
										}}
										accept={{
											'image/*': ['.jpeg', '.png', 'png', 'jpeg'],
											'application/pdf': ['.pdf'],
										}}
										invalidTypeMessage="Something went wrong"
									/>
								</Expandable>
							</OverridableWrapper>
						) : null}

						<br />
						{data?.blob && data?.fileExtension ? (
							<DocumentViewer
								pdfLoaderProps={{
									pageHeight: 560,
									pageWidth: 410,
									withBorder: true,
								}}
								file={data.blob}
								fileExtension={data.fileExtension}
							/>
						) : null}
					</Modal>
				) : null}
			</div>
		);
	}
)``;

const OverridableWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

export const GridBox = styled.div`
	display: flex;
	min-height: 10rem;
	margin-bottom: 2rem;
	overflow: auto;

	& > * {
		margin-right: 2rem;
	}
`;
